// Admin Settings → Integrations → Apple push notifications.
//
// PEXIFY's single Apple Push (.p8) key, used to send the live delivery card to
// the iPhone lock screen. Built like Research tools (ResearchTools.tsx): the same
// card, the shared locked-credential gate, and a write-only secret — after a
// save only the Key ID comes back; the .p8 contents are never shown again.

import { useRef, useState, type ChangeEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BellRing, FileUp, KeyRound, ShieldCheck } from 'lucide-react'
import { apiUrl, USE_REAL_API } from '../../lib/api'
import { formatDateTime } from '../../lib/datetime'
import { toast } from '../../lib/toast'
import { ConfiguredBadge, LockedCredentialNotice } from '../../components/credential-lock'
import { useEditGate } from '../../components/credential-lock.lib'
import { Select } from '../../components/ui/select'
import { useT } from '../../i18n'
import { applePushI18n } from '../../i18n/admin/apple-push'

type PushProviderCredentialRead = {
  id: string
  provider: 'apns'
  key_hint: string | null
  has_credentials: boolean
  created_at: string
  updated_at: string
}

type PushProviderCredentials = {
  items: PushProviderCredentialRead[]
  total: number
}

type ApnsEnvironment = 'production' | 'sandbox'

const QUERY_KEY = ['admin-push-provider-credentials'] as const
const BASE = '/admin/push-provider-credentials'
const DEFAULT_TEAM_ID = '8R8G3M6NTX'
const DEFAULT_BUNDLE_ID = 'net.pexify.app'
const APPLE_ID_LENGTH = 10
const INPUT_CLASS =
  'w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] font-mono text-[var(--color-text)] outline-none focus:border-indigo-300 focus:shadow-[0_0_0_3px_rgb(238_242_255)]'

async function fetchCredentials(): Promise<PushProviderCredentials> {
  const response = await fetch(apiUrl(BASE))
  if (!response.ok) throw new Error(`fetch push provider credentials ${response.status}`)
  return (await response.json()) as PushProviderCredentials
}

function errorMessage(body: unknown, fallback: string): string {
  if (body && typeof body === 'object' && 'detail' in body && body.detail) {
    const detail = body.detail
    if (typeof detail === 'object' && 'message' in detail && typeof detail.message === 'string') {
      return detail.message
    }
    // FastAPI 422: a list of {loc, msg}. The backend never echoes the input.
    if (Array.isArray(detail)) {
      const messages = detail
        .map((item: unknown) =>
          item && typeof item === 'object' && 'msg' in item && typeof item.msg === 'string' ? item.msg : null,
        )
        .filter((msg): msg is string => Boolean(msg))
      if (messages.length > 0) return messages.join(' · ')
    }
  }
  return fallback
}

async function request<T>(path: string, init: RequestInit): Promise<T | null> {
  const response = await fetch(apiUrl(path), init)
  if (!response.ok) {
    throw new Error(errorMessage(await response.json().catch(() => null), `request failed (${response.status})`))
  }
  return response.status === 204 ? null : ((await response.json()) as T)
}

export function ApplePushCredentials() {
  const t = useT(applePushI18n)
  const queryClient = useQueryClient()
  const query = useQuery({ queryKey: QUERY_KEY, queryFn: fetchCredentials })
  const current = query.data?.items.find((item) => item.provider === 'apns') ?? null

  return (
    <div className="space-y-4">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-[var(--color-text)]">
          <BellRing size={22} className="text-indigo-600" aria-hidden="true" />
          {t.title}
        </h1>
        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-[var(--color-text-muted)]">
          <ShieldCheck size={12} className="text-[var(--color-success)]" aria-hidden="true" />
          {t.encryptedNote}
        </p>
      </header>

      {!USE_REAL_API && (
        <div className="rounded-lg bg-[color-mix(in_srgb,var(--color-warning)_12%,var(--color-surface))] p-3 text-xs text-[var(--color-warning)] ring-1 ring-[color-mix(in_srgb,var(--color-warning)_38%,var(--color-border))]">
          {t.simulationNotice}
        </div>
      )}

      {query.isLoading ? (
        <div className="rounded-lg bg-[var(--color-surface)] p-8 text-center text-sm text-[var(--color-text-muted)] ring-1 ring-[var(--color-border)]">
          {t.loading}
        </div>
      ) : query.error ? (
        <div className="rounded-lg bg-[var(--color-surface)] p-8 text-center text-sm text-[var(--color-danger)] ring-1 ring-[var(--color-border)]">
          {t.loadError}
        </div>
      ) : (
        <ApnsCard
          key={current?.id ?? 'none'}
          current={current}
          onChanged={() => queryClient.invalidateQueries({ queryKey: QUERY_KEY })}
        />
      )}
    </div>
  )
}

function ApnsCard({
  current,
  onChanged,
}: {
  current: PushProviderCredentialRead | null
  onChanged: () => void
}) {
  const t = useT(applePushI18n)
  const configured = Boolean(current?.has_credentials)
  const gate = useEditGate(configured)
  const fileInput = useRef<HTMLInputElement>(null)
  const [p8, setP8] = useState('')
  const [keyId, setKeyId] = useState('')
  const [teamId, setTeamId] = useState(DEFAULT_TEAM_ID)
  const [bundleId, setBundleId] = useState(DEFAULT_BUNDLE_ID)
  const [environment, setEnvironment] = useState<ApnsEnvironment>('production')
  const [confirmingRemove, setConfirmingRemove] = useState(false)

  const save = useMutation({
    mutationFn: () =>
      request<PushProviderCredentialRead>(`${BASE}/apns`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          p8: p8.trim(),
          key_id: keyId.trim().toUpperCase(),
          team_id: teamId.trim().toUpperCase(),
          bundle_id: bundleId.trim(),
          environment,
        }),
      }),
    onSuccess: () => {
      setP8('')
      setKeyId('')
      gate.lock()
      onChanged()
      toast.success(t.savedToast, {
        description: t.savedToastDescription,
        audience: 'admin',
      })
    },
    onError: (error: unknown) => {
      toast.error(t.saveErrorToast, {
        description: error instanceof Error ? error.message : t.unknownError,
        audience: 'admin',
      })
    },
  })

  const remove = useMutation({
    mutationFn: () => request<null>(`${BASE}/apns`, { method: 'DELETE' }),
    onSuccess: () => {
      setConfirmingRemove(false)
      onChanged()
      toast.success(t.removedToast, { audience: 'admin' })
    },
    onError: (error: unknown) => {
      toast.error(t.removeErrorToast, {
        description: error instanceof Error ? error.message : t.unknownError,
        audience: 'admin',
      })
    },
  })

  const onFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    // Reset so choosing the same file again still fires a change.
    event.target.value = ''
    if (!file) return
    try {
      setP8((await file.text()).trim())
    } catch (error: unknown) {
      toast.error(t.fileReadError, {
        description: error instanceof Error ? error.message : t.unknownError,
        audience: 'admin',
      })
    }
  }

  const canSave =
    USE_REAL_API &&
    p8.trim().length > 0 &&
    keyId.trim().length === APPLE_ID_LENGTH &&
    teamId.trim().length === APPLE_ID_LENGTH &&
    bundleId.trim().length > 0 &&
    !save.isPending

  return (
    <section
      className="overflow-hidden rounded-lg bg-[var(--color-surface)] ring-1 ring-[var(--color-border)]"
      aria-label={t.title}
    >
      <div className="flex items-center justify-between gap-4 border-b border-[var(--color-border)] px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[color-mix(in_srgb,var(--color-info)_12%,var(--color-surface))] text-[var(--color-info)]">
            <BellRing size={18} aria-hidden="true" />
          </div>
          <div>
            <div className="text-sm font-semibold text-[var(--color-text)]">Apple Push</div>
            <div className="text-[11px] text-[var(--color-text-muted)]">{t.providerSubtitle}</div>
          </div>
        </div>
        <ConfiguredBadge configured={configured} />
      </div>

      {!gate.editing ? (
        <div className="space-y-3 px-5 py-5">
          <LockedCredentialNotice hint={current?.key_hint} onEdit={gate.requestEdit} label={t.credentialsLabel} />
          <p className="text-xs text-[var(--color-text-muted)]">{t.usageNote}</p>
          {current && (
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-[var(--color-text-muted)]">
              <span>{t.updated(formatDateTime(current.updated_at))}</span>
              {confirmingRemove ? (
                <span className="inline-flex items-center gap-2">
                  <span>{t.confirmRemove}</span>
                  <button
                    type="button"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate()}
                    className="font-semibold text-[var(--color-danger)] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {t.yesRemove}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmingRemove(false)}
                    className="font-medium text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                  >
                    {t.cancel}
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmingRemove(true)}
                  className="font-medium text-[var(--color-danger)] hover:text-[var(--color-danger)]"
                >
                  {t.remove}
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4 px-5 py-5">
          <p className="text-xs text-[var(--color-text-muted)]">{t.usageNote}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-[var(--color-text)]">{t.keyId}</span>
              <input
                type="text"
                value={keyId}
                onChange={(event) => setKeyId(event.target.value)}
                maxLength={APPLE_ID_LENGTH}
                autoComplete="off"
                spellCheck={false}
                className={INPUT_CLASS}
              />
              <span className="mt-1 block text-[11px] text-[var(--color-text-muted)]">{t.keyIdHint}</span>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-[var(--color-text)]">{t.teamId}</span>
              <input
                type="text"
                value={teamId}
                onChange={(event) => setTeamId(event.target.value)}
                maxLength={APPLE_ID_LENGTH}
                autoComplete="off"
                spellCheck={false}
                className={INPUT_CLASS}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-[var(--color-text)]">{t.bundleId}</span>
              <input
                type="text"
                value={bundleId}
                onChange={(event) => setBundleId(event.target.value)}
                autoComplete="off"
                spellCheck={false}
                className={INPUT_CLASS}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-[var(--color-text)]">{t.environment}</span>
              <Select value={environment} onChange={(event) => setEnvironment(event.target.value as ApnsEnvironment)}>
                <option value="production">{t.environmentProduction}</option>
                <option value="sandbox">{t.environmentSandbox}</option>
              </Select>
            </label>
          </div>
          <div>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-[var(--color-text)]">{t.p8Key}</span>
              <span className="mb-2 block text-[11px] text-[var(--color-text-muted)]">{t.p8KeyHint}</span>
              <textarea
                value={p8}
                onChange={(event) => setP8(event.target.value)}
                rows={6}
                placeholder="-----BEGIN PRIVATE KEY-----"
                autoComplete="off"
                spellCheck={false}
                className={INPUT_CLASS}
              />
            </label>
            <input
              ref={fileInput}
              type="file"
              accept=".p8"
              className="hidden"
              aria-hidden="true"
              tabIndex={-1}
              data-testid="apns-p8-file"
              onChange={(event) => void onFile(event)}
            />
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border-strong)] px-3 py-1.5 text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-muted)]"
            >
              <FileUp size={13} aria-hidden="true" />
              {t.chooseFile}
            </button>
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-[var(--color-border)] pt-4">
            {configured && (
              <button
                type="button"
                onClick={gate.lock}
                className="rounded-md border border-[var(--color-border-strong)] px-4 py-2 text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-muted)]"
              >
                {t.cancel}
              </button>
            )}
            <button
              type="button"
              disabled={!canSave}
              onClick={() => save.mutate()}
              className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <KeyRound size={13} aria-hidden="true" />
              {save.isPending ? t.saving : current ? t.rotateCredentials : t.saveCredentials}
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
